import React, { useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MicrofrontendScreen } from "@enpowered/microfrontends";
import { AltLayout } from "@enpowered/ui";
import { sendMessageToService } from "@enpowered/jsonrpc-client";
import { EventEmitter } from "eventemitter3";
import mixpanel from "mixpanel-browser";

import {
  getMicrofrontendManifests,
  getMicrofrontendManifestsWithOverrides
} from "./services/microfrontends.service";
import { UserContext } from "auth/contexts/user";
import { useLogout } from "lib/use-logout";
import NotFound from "not-found/not-found";
import { authedFetch } from "utils";
import { useResetTokens } from "./auth/hooks/use-reset-tokens.hook";
import { useSideNavItems } from "./hooks/use-sidenav-items.hook";
import { useQuery } from "react-query";
import reportAnalytics from "./reportAnalytics";
import { useQueryParams } from "./hooks/useQueryParams";

const eventBus = new EventEmitter();

const Loading = ({ user, navItems }) => (
  <AltLayout
    pageTitle=""
    logout={async () => {}}
    userName={user?.firstName || null}
    Link={props => <Link {...props} />}
    isUrlActive={() => false}
    items={user ? navItems : []}
  >
    <div className="spin-when-empty"></div>
  </AltLayout>
);

/** @type {React.FC<{ getMicrofrontendManifests: (manifestOverrides: string[]) => Promise<import("@enpowered/microfrontends").MicroFrontendManifest[]> }>} */
export const MicrofrontendRoutes = ({ getMicrofrontendManifests }) => {
  const location = useLocation();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const logout = useLogout();
  const homedir = `${process.env.PUBLIC_URL || ""}${location.pathname}`;
  const resetUserTokens = useResetTokens();

  const queryParams = useQueryParams(location);

  const manifestOverrides = Array.isArray(queryParams.override_manifest)
    ? queryParams.override_manifest
    : [queryParams.override_manifest].filter(Boolean);

  const navItems = useSideNavItems({
    user,
    getMicrofrontendManifests: () =>
      getMicrofrontendManifests(manifestOverrides)
  });

  useEffect(() => {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
      debug: !!process.env.REACT_APP_MIXPANEL_DEBUG_MODE
    });
  }, []);
  // begin observability tracking
  useQuery(
    ["observability"],
    () =>
      reportAnalytics()
        .then(awsRum => {
          window.awsRum = awsRum;
          awsRum.enable();
        })
        .catch(console.error),
    {
      staleTime: 1000 * 60 * 10 // refresh every 10 minutes
    }
  );
  useEffect(() => {
    /** @param {MicrofrontendEvents.AuthTokensReady} tokens */
    const onAuthTokensReady = async tokens => {
      console.log("auth:tokens:ready", tokens);

      await resetUserTokens(tokens);
      navigate("/");

      window.awsRum?.registerDomEvents([
        {
          event: "auth:tokens:ready",
          cssLocator: `[data-mf-scope="Auth"]`
        }
      ]);
    };
    eventBus.on("auth:tokens:ready", onAuthTokensReady);

    /** @param {MicrofrontendEvents.MfMount} props */
    const onMfMount = props => {
      window.awsRum?.registerDomEvents([
        {
          event: "mf:mount",
          element: props?.container,
          cssLocator: `[data-mf-scope="${props?.scope}"][data-mf-module="${props?.module}"]`
        }
      ]);
    };
    eventBus.on("mf:mount", onMfMount);

    /** @param {MicrofrontendEvents.MfMount} props */
    const onMfUnmount = props => {
      window.awsRum?.registerDomEvents([
        {
          event: "mf:unmount",
          element: props?.container,
          cssLocator: `[data-mf-scope="${props?.scope}"][data-mf-module="${props?.module}"]`
        }
      ]);
    };
    eventBus.on("mf:unmount", onMfUnmount);

    /** @param {MicrofrontendEvents.EnpoweredAccountChange} props */
    const onEnpoweredAccountChange = props => {
      mixpanel.identify(props?.id);
      mixpanel.people.set({
        $name: props?.name,
        $region: props?.businessAddress?.state,
        $city: props?.businessAddress?.city,
        Country: props?.businessAddress?.country
      });
    };
    eventBus.on(
      "customer-administration:enpowered-account:change",
      onEnpoweredAccountChange
    );

    /** @param {any} error */
    const onMfError = error => {
      window.awsRum?.recordError(error);
    };
    eventBus.on("mf:error", onMfError);

    return () => {
      eventBus.off("auth:tokens:ready", onAuthTokensReady);
      eventBus.off("mf:mount", onMfMount);
      eventBus.off("mf:unmount", onMfUnmount);
      eventBus.off("mf:error", onMfError);
      eventBus.off(
        "customer-administration:enpowered-account:change",
        onEnpoweredAccountChange
      );
    };
  }, []);
  useEffect(() => {
    eventBus.emit("location:change", location);
    window.awsRum?.recordPageView(window.location.href);
  }, [location]);
  const Fallback = ({ error }) => {
    useEffect(() => {
      eventBus.emit("mf:error", error);
    }, []);
    return <NotFound user={user} navItems={navItems} />;
  };
  return (
    <MicrofrontendScreen
      Loading={() => <Loading user={user} navItems={user ? navItems : []} />}
      Fallback={Fallback}
      getMicrofrontendManifests={() =>
        getMicrofrontendManifests(manifestOverrides)
      }
      host="internal-portal"
      eventBus={eventBus}
      rpcClient={sendMessageToService(authedFetch)}
      fetch={authedFetch}
      homedir={homedir}
      navigate={navigate}
      // @ts-ignore
      user={user}
      layout={{
        navItems,
        logout
      }}
    />
  );
};

MicrofrontendRoutes.defaultProps = {
  getMicrofrontendManifests: process.env
    .REACT_APP_DISABLE_MICROFRONTEND_MANIFEST_OVERRIDE
    ? getMicrofrontendManifests
    : getMicrofrontendManifestsWithOverrides
};
